import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import Navbar from "../NavBar";
import Footer from "../Footer";

const capabilities = [
  {
    id: "governance",
    title: "Governance Platforms",
    intro: "Systems that structure operations and enforce accountability across programs and institutions.",
    body: "They define how work is organized, how decisions are made, and how control is maintained.",
    functions: [
      "Program and operational structuring",
      "Role definition and access control",
      "Policy-aligned workflows",
      "Oversight and administrative control",
    ],
    where: "Public sector programs, regulatory systems, institutional platforms",
    image: "/images/surwash/cs_ntip.jpg",
  },
  {
    id: "workflow",
    title: "Workflow & Validation Systems",
    intro: "Systems that manage submissions, approvals, and multi-step processes with enforced logic.",
    body: "They ensure that work moves correctly, and only under the right conditions.",
    functions: [
      "Submission and approval flows",
      "Rule-based validation and control points",
      "State transitions and process enforcement",
      "Exception handling and edge-case management",
    ],
    where: "Compliance systems, operational workflows, approval-driven processes",
    image: "/images/surwash/cs_dms.jpg",
  },
  {
    id: "compliance",
    title: "Compliance & Audit Systems",
    intro: "Systems designed to ensure traceability, data integrity, and audit readiness.",
    body: "They make every action, decision, and record defensible.",
    functions: [
      "Audit trails and activity tracking",
      "Compliance validation and enforcement",
      "Record integrity and versioning",
      "Reporting for regulatory review",
    ],
    where: "Regulated environments, oversight systems, high-trust operations",
    image: "/images/surwash/cs_lasepa.jpg",
  },
  {
    id: "market",
    title: "Market Infrastructure Platforms",
    intro: "Digital platforms that support regulated markets and ecosystem coordination.",
    body: "They act as the backbone for transactions, certification, and stakeholder interaction.",
    functions: [
      "Registries and lifecycle systems",
      "Transaction tracking and validation",
      "Certification and issuance mechanisms",
      "Multi-party coordination and visibility",
    ],
    where: "Carbon markets, levy systems, regulated transaction environments",
    image: "/images/surwash/cs_surcharge.jpg",
  },
  {
    id: "dashboards",
    title: "Institutional Dashboards & Reporting Systems",
    intro: "Systems that provide clear, decision-ready visibility into operations and performance.",
    body: "They transform structured data into actionable insight.",
    functions: [
      "Real-time dashboards and analytics",
      "Operational and compliance reporting",
      "Drill-down and data exploration",
      "Performance tracking across stakeholders",
    ],
    where: "Executive reporting, program oversight, operational decision-making",
    image: "/images/surwash/cs_lagosepid.jpg",
  },
];

const frameworkSteps = [
  {
    label: "Structure",
    body: "Defining workflows and system logic",
  },
  {
    label: "Governance",
    body: "Embedding validation, control, and accountability",
  },
  {
    label: "Execution",
    body: "Delivering systems that perform under real conditions",
  },
];

const environments = [
  "Government and public sector",
  "Healthcare and public health",
  "Environmental and climate systems",
  "Regulated markets and energy",
  "Finance and revenue systems",
  "Institutional operations",
];

export default function CapabilitiesPage() {
  const { hash } = useLocation();

  useEffect(() => {
    if (!hash) return;
    const id = hash.replace("#", "");
    const el = document.getElementById(id);
    if (el) {
      setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    }
  }, [hash]);

  return (
    <section className="w-full relative bg-white text-gray-800">
      <Navbar />

      {/* Hero */}
      <section className="relative min-h-[460px] bg-[#101722] text-white overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{ backgroundImage: "url('/images/sliders/_workflow.jpg')" }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#101722] via-[#101722]/90 to-[#101722]/60" />
        <div className="relative z-10 max-w-6xl mx-auto px-6 lg:px-20 py-28">
          <p className="text-sm uppercase tracking-[0.2em] text-[#44c4bd] font-semibold mb-5">
            Core Capability Areas
          </p>
          <h1 className="text-4xl md:text-6xl font-bold leading-tight max-w-4xl">
            The Systems We Design and Deliver
          </h1>
          <p className="mt-6 max-w-3xl text-lg text-white/75 leading-relaxed">
            We design and deliver systems where workflow, validation, and governance must
            operate as a single, structured whole.
          </p>
          <p className="mt-3 max-w-3xl text-white/60 leading-relaxed">
            These are not service lines. They are classes of systems built to function in
            complex, multi-stakeholder environments.
          </p>
        </div>
      </section>

      {/* How to Read This Page */}
      <div className="bg-[#f5f7fa] border-b border-gray-200 px-6 lg:px-20 py-6">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row sm:items-center gap-3">
          <span className="text-[#007681] font-bold text-sm uppercase tracking-widest shrink-0">
            How to Read This Page
          </span>
          <span className="hidden sm:block text-gray-300">|</span>
          <p className="text-gray-600 text-sm leading-relaxed">
            Each capability represents a distinct system type. In practice, these capabilities
            are combined to form complete, governance-grade platforms.
          </p>
        </div>
      </div>

      {/* Capability sections — alternating layout */}
      {capabilities.map((cap, idx) => (
        <CapabilitySection key={cap.id} cap={cap} idx={idx} />
      ))}

      {/* How These Capabilities Work Together */}
      <section className="bg-[#1C1F2B] text-white py-20 px-6 lg:px-20">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-14 items-start">
          <div>
            <p className="text-sm uppercase tracking-[0.2em] text-[#44c4bd] font-bold mb-4">
              How These Capabilities Work Together
            </p>
            <h2 className="text-3xl lg:text-4xl font-bold mb-5 leading-tight">
              No real system exists in isolation.
            </h2>
            <p className="text-white/60 leading-relaxed mb-8">
              A typical platform combines multiple capabilities to form a coherent system that
              operates reliably under real conditions.
            </p>
            <ul className="space-y-3">
              {[
                "Workflows define how work moves",
                "Validation ensures correctness",
                "Governance enforces accountability",
                "Audit systems ensure traceability",
                "Dashboards provide visibility",
              ].map((item) => (
                <li key={item} className="flex items-start gap-3 text-white/80">
                  <span className="text-[#44c4bd] mt-0.5 shrink-0">▸</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-white/5 border border-white/10 p-8">
            <p className="text-sm uppercase tracking-widest text-[#44c4bd] font-bold mb-4">
              This Is Not a Service Catalog
            </p>
            <p className="text-white/70 leading-relaxed mb-6">
              We do not offer isolated services or disconnected features. We design and
              deliver integrated systems where:
            </p>
            <ul className="space-y-3">
              {[
                "Structure is defined",
                "Control is enforced",
                "Execution is reliable",
                "Outcomes are traceable",
              ].map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <span className="text-[#E4002B] mt-0.5 shrink-0">▸</span>
                  <span className="text-white/85 font-medium">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Built on the Systems Execution Framework */}
      <section className="bg-[#f5f7fa] py-16 px-6 lg:px-20 border-t border-gray-100">
        <div className="max-w-6xl mx-auto">
          <p className="text-sm uppercase tracking-[0.18em] text-[#007681] font-bold mb-3">
            Built on the Systems Execution Framework
          </p>
          <h2 className="text-3xl lg:text-4xl font-bold mb-10">
            These capabilities are applied through a structured delivery approach.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {frameworkSteps.map((step, i) => (
              <div key={step.label} className="bg-white border border-gray-100 shadow-sm p-8">
                <p className="text-[#44c4bd] font-bold text-3xl mb-4">{i + 1}</p>
                <h3 className="text-xl font-bold mb-3 text-[#1C1F2B]">{step.label}</h3>
                <p className="text-gray-600 leading-relaxed">{step.body}</p>
              </div>
            ))}
          </div>
          <div className="mt-8">
            <Link
              to="/framework"
              className="text-sm text-[#007681] hover:text-[#E4002B] transition-colors font-semibold flex items-center gap-1.5"
            >
              → Explore the Systems Execution Framework
            </Link>
          </div>
        </div>
      </section>

      {/* Applied Across Operational Environments */}
      <section className="bg-white py-16 px-6 lg:px-20 border-t border-gray-100">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-14 items-start">
          <div>
            <p className="text-sm uppercase tracking-[0.18em] text-[#007681] font-bold mb-3">
              Applied Across Operational Environments
            </p>
            <h2 className="text-3xl lg:text-4xl font-bold mb-5 leading-snug">
              These capabilities are deployed across environments where systems must hold.
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {environments.map((env) => (
              <div key={env} className="border-l-4 border-[#007681] bg-[#f5f7fa] p-4">
                <span className="text-gray-800 font-medium capitalize">{env}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="max-w-6xl mx-auto mt-8">
          <Link
            to="/environments"
            className="text-sm text-[#007681] hover:text-[#E4002B] transition-colors font-semibold flex items-center gap-1.5"
          >
            → Explore Operational Environments
          </Link>
        </div>
      </section>

      {/* See These Capabilities in Practice */}
      <section className="bg-[#101722] text-white py-20 px-6 lg:px-20">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-sm uppercase tracking-[0.2em] text-[#44c4bd] font-semibold mb-5">
            See These Capabilities in Practice
          </p>
          <h2 className="text-3xl md:text-4xl font-bold max-w-2xl mx-auto leading-tight mb-12">
            Each capability has been applied in real, high-stakes institutional environments.
          </h2>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link
              to="/projects"
              className="bg-[#E4002B] text-white px-8 py-4 font-semibold text-sm hover:bg-red-700 transition-colors duration-200"
            >
              View Selected Case Studies →
            </Link>
            <Link
              to="/environments"
              className="border border-white text-white px-8 py-4 font-semibold text-sm hover:bg-white hover:text-[#101722] transition-colors duration-200"
            >
              Explore Operational Environments →
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </section>
  );
}

function CapabilitySection({ cap, idx }) {
  const isReversed = idx % 2 === 1;

  return (
    <section
      id={cap.id}
      className={`py-20 px-6 lg:px-20 border-t border-gray-100 scroll-mt-20 ${
        idx % 2 === 0 ? "bg-white" : "bg-[#f5f7fa]"
      }`}
    >
      <div
        className={`max-w-6xl mx-auto flex flex-col ${
          isReversed ? "lg:flex-row-reverse" : "lg:flex-row"
        } gap-12 items-center`}
      >
        {/* Image */}
        <div className="w-full lg:w-1/2">
          <img
            src={cap.image}
            alt={cap.title}
            className="w-full h-72 lg:h-96 object-cover rounded-sm shadow-md"
          />
        </div>

        {/* Content */}
        <div className="w-full lg:w-1/2">
          <p className="text-sm uppercase tracking-[0.18em] text-[#007681] font-bold mb-3">
            {cap.title}
          </p>
          <h2 className="text-2xl lg:text-3xl font-bold mb-3 leading-snug">{cap.intro}</h2>
          <p className="text-gray-500 text-base mb-8 leading-relaxed">{cap.body}</p>

          <div className="mb-6">
            <p className="text-xs uppercase tracking-widest text-gray-400 font-semibold mb-3">
              Typical Functions
            </p>
            <ul className="space-y-2">
              {cap.functions.map((fn) => (
                <li key={fn} className="flex items-start gap-2 text-gray-700 text-sm">
                  <span className="text-[#E4002B] mt-0.5 shrink-0">▸</span>
                  <span>{fn}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="border-t border-gray-200 pt-5">
            <p className="text-xs uppercase tracking-widest text-gray-400 font-semibold mb-1">
              Where It Shows Up
            </p>
            <p className="text-gray-600 text-sm">{cap.where}</p>
          </div>
        </div>
      </div>
    </section>
  );
}
